// 札 1 件の入力値 (個別作成 / 編集モーダル) が「売買逆転」「JEPX 制度上限違反」に
// 当たっているかを、 入力途中でも判定するための model。
//
// design SoT: `design/bidding/spot.html` の `_renderBnewRowAlert`
// (「2026-06-01 ユーザー指摘: 行 alert に対する『次なにをすべきか』 を modal で誘導」)。
//
// [design 差異] 8080 は **行クリックで編集モーダルを開いた瞬間**の行の状態
// (`prefill.alertInfo`) を再掲するだけだが、 実装は**入力値から都度判定**する。
// 一覧の行クリックで編集を開く導線が実装にまだ無い (#5356) ことに加え、 入力中に
// 逆転へ入った瞬間に出た方が「作ってから怒られる」より早く気付けるため。
// 判定規則そのもの (逆転 = 買値 ≧ 売値、 成行は対向札があれば交差 / JEPX 上下限・刻み) は
// 一覧と同じ `spotBidValidation` の `crossesSpotPrice` / `detectJepxLimitViolations` を
// 通すので、 一覧の警告アイコンとモーダルの警告が食い違うことはない。
// 相手にする札の範囲 (検知対象プールの状態) は呼び出し元が `isInSpotInversionPool` で絞る。
// ただし**逆転は「当事者かどうか」を自前で見る**必要がある — `detectInversions` は
// グループ単位 (同エリア・同コマ) の検出で、 逆転があるとグループの全札 id を返すため、
// 「返り値に自分が含まれるか」では下書き自身が当事者かを判定できない。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingSpotOrderMethod } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'

import { isMarketOrderRow } from '~/pages/SpotBidding/models/spotBidRow'
import type {
  InversionInput,
  JepxLimitViolationKind,
  SpotCrossPrice,
} from '~/pages/SpotBidding/models/spotBidValidation'
import {
  crossesSpotPrice,
  detectJepxPriceViolationKind,
  detectJepxVolumeViolationKind,
} from '~/pages/SpotBidding/models/spotBidValidation'

/** 判定対象の札 1 件 (モーダルのフォーム値)。 未入力の項目は undefined。 */
export type BidFormValues = {
  areaCode: AreaCode | undefined
  timeCode: number | undefined
  direction: Side | undefined
  /** 円/kWh。 */
  priceYenPerKwh: number | undefined
  /** MW (= 1 コマ 30 分の平均電力)。 */
  volumeMw: number | undefined
  /**
   * 価格種別 (指値 / 成行、 AC-BSP-004a)。 成行は価格を持たないため、 JEPX 価格上限違反の
   * 判定対象から除外する (AC-BSP-050「成行注文は価格の値域・最小単位チェックの対象外と
   * する（数量のチェックは成行にも適用する）」)。 売買逆転は成行も判定する: 対向札が
   * あれば価格を見ずに交差 (AC-BSP-009)。 undefined は指値扱い (= 一括作成モーダル等、
   * 価格種別を持たない呼び出し元との後方互換)。
   */
  orderMethod?: BiddingSpotOrderMethod
}

export type BidFormAlerts = {
  /** 同エリア・同コマで反対方向の札と価格が逆転している。 */
  isInversion: boolean
  /**
   * JEPX 制度上限の違反種別 (価格・数量、 上下限と刻み)。
   * 表示に必要なのは種別だけなので、 エリア・コマ等のメタデータは持たない
   * (= それらが未確定でも判定できるようにするため。 PR #5873 codex-review 指摘)。
   */
  jepxViolationKinds: JepxLimitViolationKind[]
}

/**
 * 編集中の札を他の札から区別するための id。 実在の札 id と衝突しない専用文字列で、
 * 判定にしか使わない (画面にも保存値にも出ない)。
 */
const DRAFT_ID = '__bid-form-draft__'

/**
 * 入力値が逆転 / JEPX 制度上限違反に当たっているかを判定する。
 *
 * @param values - モーダルのフォーム値
 * @param siblingBids - 同じ受渡日の他の札 (編集中の札自身を含んでいてよい)
 * @param editingBidId - 編集モードの場合、 編集対象の札 id。 自分自身との比較を避ける
 */
export const detectBidFormAlerts = (
  values: BidFormValues,
  siblingBids: readonly InversionInput[],
  editingBidId?: string,
): BidFormAlerts => {
  const {
    areaCode,
    timeCode,
    direction,
    priceYenPerKwh,
    volumeMw,
    orderMethod,
  } = values
  // 成行 (AC-BSP-004a) は価格を持たない。 JEPX 価格違反は「価格」が前提の判定なので
  // 成行の間は丸ごとスキップする (AC-BSP-050)。 一方 売買逆転 (AC-BSP-009) は
  // 「成行は対向札があれば交差」 と明記されており (spec Discussion 2026-09-08 決定)、
  // 成行だからと逆転判定自体をスキップしてはいけない。
  const isMarket = orderMethod === BiddingSpotOrderMethod.MARKET

  // 逆転はエリア・コマ・方向が確定して初めて判定できる。 指値は価格も確定が必要
  // (成行は価格を持たないので price の確定は問わない)。
  const canJudgeInversion =
    areaCode !== undefined &&
    timeCode !== undefined &&
    direction !== undefined &&
    (isMarket ||
      (priceYenPerKwh !== undefined && Number.isFinite(priceYenPerKwh)))

  let isInversion = false
  if (canJudgeInversion) {
    // 編集中の札は「変更前の値」として残っているので必ず除く
    // (除かないと自分自身の旧価格と突き合わせて誤検知する)。
    const opponents = siblingBids.filter(
      (b) =>
        b.id !== editingBidId &&
        b.id !== DRAFT_ID &&
        b.areaCode === areaCode &&
        b.timeCode === timeCode &&
        b.direction !== direction,
    )

    // 対向札のうち、 成行があれば成行、 なければ自分に最も近い指値 (自分が買なら売の最安値、
    // 売なら買の最高値) と比べる。 成行の規則は一覧と同じ `crossesSpotPrice` (AC-BSP-009)。
    // ⚠ `detectInversions` の結果に下書き id が含まれるかでは判定できない。
    // あれは「同エリア・同コマのグループ内に逆転がある」時にグループの**全札 id** を
    // 返すので、 既存札どうしが逆転しているだけで下書きも当事者扱いになり、
    // 下書きの価格をいくら直しても警告が消えなくなる (PR #5873 の Devin Review 指摘)。
    // 下書き自身が当事者かを、 反対方向の札との比較で直接見る。
    const opponentPrices = opponents
      .filter((b) => !isMarketOrderRow(b))
      .map((b) => Number(b.priceYenPerKwh))
      .filter((v) => Number.isFinite(v))
    const hasMarketOpponent = opponents.some(isMarketOrderRow)
    const own: SpotCrossPrice | undefined = isMarket ? 'market' : priceYenPerKwh
    let opponent: SpotCrossPrice | undefined
    if (hasMarketOpponent) {
      opponent = 'market'
    } else if (opponentPrices.length > 0) {
      opponent =
        direction === Side.BUY
          ? Math.min(...opponentPrices)
          : Math.max(...opponentPrices)
    }
    if (own !== undefined && opponent !== undefined) {
      isInversion =
        direction === Side.BUY
          ? crossesSpotPrice(own, opponent)
          : crossesSpotPrice(opponent, own)
    }
  }

  // JEPX 制度上限は**エリア・コマ・売買方向に依存しない** (制度の値域と刻みだけ)。
  // よってそれらが未確定でも、 価格 / 数量が入った時点で判定する
  // (spec AC-BSP-050「入力段階でエラー表示」。 軸の選択を待つと、 先に価格を
  // 入れた時に何も出ない。 PR #5873 codex-review 指摘)。
  const jepxViolationKinds = [
    !isMarket && priceYenPerKwh !== undefined
      ? detectJepxPriceViolationKind(priceYenPerKwh)
      : undefined,
    volumeMw !== undefined
      ? detectJepxVolumeViolationKind(volumeMw)
      : undefined,
  ].filter((k): k is JepxLimitViolationKind => k !== undefined)

  return { isInversion, jepxViolationKinds }
}
